import { HistoryBadgeFilter } from '@components/HistoryBadgeFilter';
import { HistoryDateRangeFilter } from '@components/HistoryDateRangeFilter';
import type { UerBaseCostChangeType } from '@gql/graphql';
import { AgGridTable } from 'components/AgGrid/AgGridTable';
import { ModalDialog } from 'components/ModalDialog';
import { usePageFilterSaver } from 'hooks/usePageFilterSaver';
import { Button, cnMixSpace, Text } from 'uikit';

import { useMemo, useCallback } from 'react';

import Style from '../common/catalogUerBaseHistory.module.css';

import { historyStylesRules } from './helpers';
import { useExportHistoryAsExcel, useHistoryColumns, useHistoryData } from './hooks';

interface ICatalogUerBaseCostHistory {
    closeHistory: () => void;
    catalogId: string | undefined;
}

export const CatalogUerBaseCostHistory = ({ closeHistory, catalogId }: ICatalogUerBaseCostHistory) => {
    const tableUniqueId = useMemo(() => `CatalogUerBaseCostHistory_${catalogId}`, [catalogId]);

    const { dataSource, tableRef, handlerFilterChange, countsObj, tableRdy, dateFilterValues } =
        useHistoryData(catalogId);

    const columns = useHistoryColumns({
        handlerFilterChange,
        catalogId,
        tableRdy,
        dateFilterValues,
    });

    const { exportHistoryHandler, isExportHistoryInProgress, isHandlerDisabled } = useExportHistoryAsExcel({
        catalogId,
        dateFilterValues,
    });
    const displayedRowCount = tableRef?.current?.api?.getDisplayedRowCount() ?? 0;

    const titleToolBar = useMemo(
        () => (
            <div className={Style.titleBlock}>
                <div className={Style.titleLeft}>
                    <Text
                        view="secondary"
                        lineHeight="l"
                        size="s"
                        weight="semibold"
                        transform="uppercase"
                        className={cnMixSpace({
                            mR: 's',
                        })}
                    >
                        Просмотр истории изменений
                    </Text>

                    <HistoryDateRangeFilter onDateRangeChange={handlerFilterChange} />

                    <Button
                        label="Выгрузить отчет"
                        size="s"
                        view="secondary"
                        loading={isExportHistoryInProgress}
                        disabled={isHandlerDisabled || !displayedRowCount}
                        onClick={exportHistoryHandler}
                        className={cnMixSpace({
                            mL: 's',
                        })}
                    />
                </div>
                <HistoryBadgeFilter<UerBaseCostChangeType>
                    handlerBadgeFilterChange={handlerFilterChange}
                    countsObj={countsObj}
                />
            </div>
        ),
        [
            countsObj,
            displayedRowCount,
            exportHistoryHandler,
            handlerFilterChange,
            isExportHistoryInProgress,
            isHandlerDisabled,
        ],
    );

    const { handleSaveFilterState } = usePageFilterSaver({});

    const closeHandlerWithFilterCleaner = useCallback(() => {
        closeHistory();

        handleSaveFilterState({
            key: tableUniqueId,
            newState: undefined,
        });
    }, [closeHistory, handleSaveFilterState, tableUniqueId]);

    return (
        <ModalDialog
            isOpen
            title={titleToolBar}
            onClose={closeHandlerWithFilterCleaner}
            dockedToWindow="bottom"
            withTitleCloseButton
            isOnClickOutsideDisable
            withBottomButtons={false}
        >
            <div className={Style.modalBody}>
                {!tableRdy && (
                    <Text view="secondary" size="xs" className={Style.tableOverlay}>
                        Для отображения данных выберите период
                    </Text>
                )}

                <AgGridTable
                    ref={tableRef}
                    className={Style.historyTable}
                    datasource={dataSource}
                    rowModelType="infinite"
                    maxConcurrentDatasourceRequests={1}
                    maxBlocksInCache={50}
                    cacheBlockSize={50}
                    columnDefs={columns}
                    headerHeight={40}
                    rowClassRules={historyStylesRules}
                    suppressNoRowsOverlay
                    context={{ tableUniqueId }}
                />
            </div>
        </ModalDialog>
    );
};

CatalogUerBaseCostHistory.displayName = 'CatalogUerBaseCostHistory';
